// src/features/project/AddItemDialog.tsx
// Add work to the project: create a fresh task right here (the frequent
// path), or attach an existing product task picked from a searchable list.
// Nobody types raw ids — the product comes from the scope, tasks from the
// task service.

import { useEffect, useMemo, useState } from 'react';
import { useI18n, useProjectItem, useTask } from '@react/hooks';
import { Button, Input, ErrorAlert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import { ProjectModal, Field } from './ProjectModal';
import { entityRef } from './meta';
import type { Entity } from '@core/types/entity';
import type { Task } from '@core/types/task';

interface AddItemDialogProps {
  open: boolean;
  projectEntity: Entity;
  defaultColumnId?: string;
  onClose: () => void;
  onAdded: () => void;
}

type Mode = 'create' | 'attach';

export function AddItemDialog({ open, projectEntity, defaultColumnId, onClose, onAdded }: AddItemDialogProps) {
  const { t } = useI18n();
  const itemStore = useProjectItem();
  const taskStore = useTask();
  const scope = useScope();

  const [mode, setMode] = useState<Mode>('create');
  const [title, setTitle] = useState('');
  const [query, setQuery] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [points, setPoints] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const productId = projectEntity.product_id || scope.productId;

  // Load the product's tasks once the attach mode is opened.
  useEffect(() => {
    if (!open || mode !== 'attach' || tasks.length > 0) return;
    setLoadingTasks(true);
    taskStore
      .ListTasks({
        pagination: { cursor: '', limit: 100, direction: 'forward' as const },
        sort: [],
        view: 0,
      } as never)
      .then(() => setTasks([...taskStore.items]))
      .catch(() => { /* empty list state below */ })
      .finally(() => setLoadingTasks(false));
  }, [open, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredTasks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tasks.slice(0, 20);
    return tasks.filter((task) => (task.title || '').toLowerCase().includes(q)).slice(0, 20);
  }, [tasks, query]);

  const reset = () => {
    setTitle('');
    setQuery('');
    setSelectedTaskId('');
    setPoints('');
    setSubmitError(null);
  };

  const attachTask = async (taskId: string, taskTitle: string) => {
    await itemStore.AddItem(projectEntity.id, {
      project: projectEntity,
      source_task: entityRef(taskId, 'task', projectEntity),
      source_product: entityRef(productId, 'product', projectEntity),
      column_id: defaultColumnId || undefined,
      story_points: points === '' ? undefined : Number(points),
      title: taskTitle,
    } as never);
  };

  const submit = async () => {
    setSubmitError(null);
    setBusy(true);
    try {
      if (mode === 'create') {
        if (!title.trim()) return;
        const task = await taskStore.CreateTask({
          title: title.trim(),
          parent: projectEntity,
          tags: [],
          initial_custom_fields: [],
          initial_checklists: [],
        } as never);
        const newId = task?.entity?.id;
        if (!newId) throw new Error(t('project.item.createFailed'));
        await attachTask(newId, task.title || title.trim());
      } else {
        if (!selectedTaskId) return;
        const selectedTask = tasks.find((task) => task.entity?.id === selectedTaskId);
        if (!selectedTask) throw new Error(t('project.item.noTasks'));
        await attachTask(selectedTaskId, selectedTask.title || '');
      }
      reset();
      onAdded();
      onClose();
    } catch (e) {
      setSubmitError(e);
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = mode === 'create' ? title.trim().length > 0 : selectedTaskId !== '';

  return (
    <ProjectModal
      open={open}
      title={t('project.item.addTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !canSubmit}>
            {busy ? t('common.loading') : t('project.item.add')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Mode switch */}
        <div className="flex gap-1 rounded-lg bg-muted p-1" role="tablist" aria-label={t('project.item.addTitle')}>
          {(['create', 'attach'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => { setMode(m); setSubmitError(null); }}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                mode === m ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(m === 'create' ? 'project.item.modeCreate' : 'project.item.modeAttach')}
            </button>
          ))}
        </div>

        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}

        {mode === 'create' ? (
          <Field label={t('project.item.newTaskTitle')} htmlFor="add-item-title">
            <Input
              id="add-item-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('project.item.newTaskPlaceholder')}
            />
          </Field>
        ) : (
          <div className="space-y-2">
            <Field label={t('project.item.pickTask')} htmlFor="add-item-search">
              <Input
                id="add-item-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('common.search')}
              />
            </Field>
            <div className="max-h-48 overflow-y-auto rounded-lg border border-border divide-y divide-border/60">
              {loadingTasks ? (
                <div className="p-3 space-y-2" aria-hidden="true">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-4 rounded bg-muted animate-pulse" />
                  ))}
                </div>
              ) : filteredTasks.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">{t('project.item.noTasks')}</p>
              ) : (
                filteredTasks.map((task) => {
                  const id = task.entity?.id ?? '';
                  const active = id === selectedTaskId;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSelectedTaskId(active ? '' : id)}
                      aria-pressed={active}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                        active ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <span className="block truncate">{task.title}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        <Field label={t('project.item.storyPoints')} htmlFor="add-points">
          <Input
            id="add-points"
            inputMode="numeric"
            value={points}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || /^\d+$/.test(v)) setPoints(v);
            }}
            placeholder="—"
          />
        </Field>
      </div>
    </ProjectModal>
  );
}
